import { logException } from '../log/logException';
import { capitalizeFirstLetter } from '../string/capitalizeFirstLetter';
import { cleanUpLocaleDateString } from './cleanUpLocaleDateString';

/**
 * Since some of the translations are not quite what we want our users to see, we need to tweak
 * the output a little bit, depending on the format. And since these formatting options are language
 * specific, we define a list of given languages where we know FOR A FACT that we want to change
 * the output text and that we know FOR A FACT how they should be changed.
 *
 * You will notice that this is not an extensive list of languages, we are just formatting the most used
 * languages (mostly because these are the ones that we know).
 */
const formatters: Record<string, Record<string, (tz: string) => string>> = {
    longGeneric: {
        fr: (timeZoneName: string) => capitalizeFirstLetter(timeZoneName.replace('heure : ', '')),
        es: (timeZoneName: string) => capitalizeFirstLetter(timeZoneName.replace('hora de ', '')),
        ja: (timeZoneName: string) => timeZoneName.replace(/^日/, ''),
    },
    shortGeneric: {
        fr: (timeZoneName: string) => timeZoneName.replace('heure : ', ''),
        es: (timeZoneName: string) => timeZoneName.replace('hora de ', ''),
        en: (timeZoneName: string) => timeZoneName.replace(' Time', ''),
        ja: (timeZoneName: string) => timeZoneName.replace(/^日/, ''),
    },
};

export const getTimeZoneName = ({
    date,
    timeZone,
    language,
    format = 'longGeneric',
    withLanguageFormatting = true,
}: {
    /** optional date used for retrieving the timezone */
    date?: Date;
    /** given time zone */
    timeZone: string;
    /** language in which we want the time zone to be translated into */
    language: string;
    /** the format of the time zone's name. `longGeneric` returns Central European Time while `shortGeneric` returns `France Time` */
    format?: 'longGeneric' | 'shortGeneric';
    /** whether the language specific formatting should be applied. If set to false, this method returns `France Time` instead of `France` */
    withLanguageFormatting?: boolean;
}) => {
    try {
        const timeZoneName = cleanUpLocaleDateString(
            (date || new Date()).toLocaleDateString(language, {
                /** This forces the output of toLocaleDateString to a fixed amount of digits */
                day: '2-digit',
                timeZoneName: format,
                timeZone,
            }),
        );

        return formatters[format][language] && withLanguageFormatting
            ? formatters[format][language](timeZoneName)
            : timeZoneName;
    } catch (excp) {
        logException(excp);
    }

    return '';
};
